import { default as IconClose, default as IconX } from "@/assets/icons/ic-close.svg";
import IconUpload from "@/assets/icons/ic-upload.svg";
import { ConfirmDialog } from "@/components/common/confirm-dialog";
import { Button } from "@/components/ui/button";
import { DateTimePicker } from "@/components/ui/date-picker/date-picker";
import { Input } from "@/components/ui/input";
import { RichTextEditor } from "@/components/ui/rich-text-editor";
import { SafeImage } from "@/components/ui/safe-image/safe-image";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { usePermissions } from "@/lib/permissions";
import { AdminAxNewsService, ICategory } from "@/services/ax-news-service";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { z } from "zod";

import IconSave from "@/components/icons/icon-save";
import React from "react";

interface LabelProps {
  children: React.ReactNode;
  required?: boolean;
  className?: string;
}

export const Label: React.FC<LabelProps> = ({ children, required = false, className = "" }) => {
  return (
    <label className={`text-[14px] font-normal text-[#000000] ${className}`}>
      {children}
      {required && <span className="ml-1 text-[#D73D1D]">*</span>}
    </label>
  );
};

const CATEGORY_OPTIONS = [
  { value: "ai_use_case", label: "AI Use Case" },
  { value: "ax_trend", label: "AX Trend" },
  { value: "pwc_digital_solutions", label: "PwC Digital Solutions" },
];

// ⭐️ Zod 스키마 수정: File 객체이거나 string(기존 이미지 URL)인 경우 모두 통과하도록 수정
const articleFormSchema = z.object({
  title: z.string().min(1, "제목을 입력해 주세요."),
  category: z.string().min(1, "카테고리를 선택해 주세요."),
  content: z.string().refine(
    (html) => {
      const text = html
        .replace(/<[^>]*>/g, "")
        .replace(/&nbsp;/g, "")
        .trim();

      return text.length > 0;
    },
    {
      message: "내용을 입력해 주세요.",
    },
  ),
  coverImage: z
    .any()
    .refine((file) => file instanceof File || (typeof file === "string" && file.length > 0), {
      message: "커버 이미지를 업로드해 주세요.",
    }),
  publish_at: z.string().optional(),
});

type ArticleFormData = z.infer<typeof articleFormSchema>;
type PageMode = "create" | "edit";

const getPageMode = (id?: string): PageMode => {
  return id ? "edit" : "create";
};

const AxNewsForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { can } = usePermissions();
  const canSubmit = id ? can.update("ax_trend_hub") : can.create("ax_trend_hub");

  const [pageMode, setPageMode] = useState<PageMode>("create");
  const [coverImagePreview, setCoverImagePreview] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [articleUpdatedAt, setArticleUpdatedAt] = useState<string>("");
  const [showDuplicateDialog, setShowDuplicateDialog] = useState(false);
  const [pendingCreateData, setPendingCreateData] = useState<ArticleFormData | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [coverImageError, setCoverImageError] = useState<string>("");
  const coverReplaceInputRef = useRef<HTMLInputElement>(null);

  const isCreate = pageMode === "create";
  const isEdit = pageMode === "edit";

  const form = useForm<ArticleFormData>({
    resolver: zodResolver(articleFormSchema),
    mode: "onSubmit",
    defaultValues: {
      title: "",
      category: "ai_use_case",
      content: "",
      coverImage: undefined,
      publish_at: "",
    },
  });

  const { reset } = form;

  // Detect mode from URL path
  useEffect(() => {
    setPageMode(getPageMode(id));
  }, [id]);

  // Fetch article detail
  useEffect(() => {
    if (!id || isCreate) return;
    AdminAxNewsService.getById(id)
      .then((res) => {
        if (res.data?.success && res.data?.data) {
          const d = res.data.data;
          setCoverImagePreview(d.cover_image_url || "");
          setArticleUpdatedAt(d.updated_at || "");
          reset({
            title: d.title,
            category: d.category as ICategory,
            content: d.content,
            publish_at: d.publish_at || "",
            coverImage: d.cover_image_url, // ⭐️ API에서 내려온 string URL을 폼 상태에 그대로 채워넣음
          });
        }
      })
      .catch(() => {
        toast({
          description: "글 정보를 불러오는데 실패했습니다.",
          variant: "destructive",
        });
      });
  }, [id, isCreate, reset, toast]);

  const MAX_FILE_SIZE = 10 * 1024 * 1024;
  const ALLOWED_TYPES = ["image/png", "image/jpg", "image/jpeg", "image/gif"];

  const handleCoverImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];

    if (!file) return;

    const isInvalidType = !ALLOWED_TYPES.includes(file.type);
    const isOverSize = file.size > MAX_FILE_SIZE;

    if (isInvalidType && isOverSize) {
      setCoverImageError("파일/이미지는 PNG, JPG, JPEG 형식이며 10MB 이하여야 합니다.");
      return;
    }

    if (isInvalidType) {
      setCoverImageError("PNG, JPG, JPEG, GIF 형식의 파일/이미지만 업로드 가능합니다.");
      return;
    }

    if (isOverSize) {
      setCoverImageError("10MB 이하의 파일/이미지만 업로드 가능합니다.");
      return;
    }

    setCoverImageError("");
    form.setValue("coverImage", file, {
      shouldValidate: true,
    });

    setCoverImagePreview(URL.createObjectURL(file));
  };

  const handleRemoveCoverImage = () => {
    form.setValue("coverImage", undefined, { shouldValidate: true });
    setCoverImagePreview("");
  };

  const handleDrop = (e: React.DragEvent<HTMLElement>) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (!file) return;

    const isInvalidType = !ALLOWED_TYPES.includes(file.type);
    const isOverSize = file.size > MAX_FILE_SIZE;

    if (isInvalidType && isOverSize) {
      setCoverImageError("파일/이미지는 PNG, JPG, JPEG 형식이며 10MB 이하여야 합니다.");
      return;
    }

    if (isInvalidType) {
      setCoverImageError("PNG, JPG, JPEG, GIF 형식의 파일/이미지만 업로드 가능합니다.");
      return;
    }

    if (isOverSize) {
      setCoverImageError("10MB 이하의 파일/이미지만 업로드 가능합니다.");
      return;
    }

    setCoverImageError("");
    form.setValue("coverImage", file, { shouldValidate: true });
    setCoverImagePreview(URL.createObjectURL(file));
  };

  const handleDragOver = (e: React.DragEvent<HTMLElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLElement>) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const onSubmit = async (data: ArticleFormData, force = false) => {
    setIsSubmitting(true);
    try {
      const payload = {
        title: data.title.trim(),
        category: data.category as ICategory,
        content: data.content,
        publish_at: data.publish_at || undefined,
      };

      if (isCreate) {
        await AdminAxNewsService.create(
          {
            ...payload,
            cover_image: data.coverImage,
          },
          force,
        );

        toast({
          description: "새 게시글이 생성되었습니다.",
        });

        navigate("/ax-trend-hub");
      }

      if (isEdit && id) {
        await AdminAxNewsService.update(id, {
          ...payload,
          // ⭐️ 사용자가 이미지를 새로 변경했을 때(즉, File 인스턴스일 때)만 멀티파트 body에 얹어 보냄
          ...(data.coverImage instanceof File
            ? {
                cover_image: data.coverImage,
              }
            : {}),
          updated_at: articleUpdatedAt || undefined,
        });

        toast({
          description: "게시글이 수정되었습니다.",
        });

        navigate("/ax-trend-hub");
      }
    } catch (err: any) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;

      if (status === 409) {
        if (detail?.code === "DUPLICATE_TITLE") {
          setPendingCreateData(data);
          setShowDuplicateDialog(true);
          return;
        }
        toast({
          description:
            typeof detail === "string"
              ? detail
              : "다른 관리자에 의해 게시글이 이미 변경되었습니다. 새로고침 후 다시 수정해 주세요.",
          variant: "destructive",
        });
        return;
      }

      toast({
        description: "저장에 실패했습니다.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleForceCreate = async () => {
    setShowDuplicateDialog(false);
    if (pendingCreateData) {
      await onSubmit(pendingCreateData, true);
      setPendingCreateData(null);
    }
  };
  const handleBack = () => navigate("/ax-trend-hub");

  return (
    <div className="flex flex-col gap-6 max-w-[896px] mx-auto pb-[64px]">
      {/* Top Bar */}
      <div className="flex items-center justify-end gap-2">
        <Button
          type="button"
          variant="secondary"
          onClick={handleBack}
          className="h-12 min-w-[120px] px-4 rounded-[8px] bg-[#EBEBEB] text-[#000000] font-bold text-base gap-2"
        >
          <img src={IconClose} alt="close" className="w-5 h-5" />
          취소
        </Button>
        {canSubmit && (
          <Button
            type="button"
            onClick={form.handleSubmit((data) => onSubmit(data))}
            disabled={isSubmitting || !form.formState.isValid}
            className="h-12 min-w-[120px] px-4 rounded-[8px] bg-primary text-black font-bold text-base gap-2 "
          >
            <IconSave fill="currentColor" className="disabled:fill-[#B0B0B0]" />
            {isCreate ? "등록" : "저장"}
          </Button>
        )}
      </div>

      {/* Main Content */}
      <div className="flex flex-col gap-6 bg-white">
        {/* heaeder */}
        <div className="text-[20px] font-bold">
          {pageMode == "edit" ? "게시글 수정" : "새 게시글 등록"}
        </div>
        {/* Category */}
        <div className="flex flex-col gap-2">
          <Label required>카테고리</Label>
          <Select
            value={form.watch("category")}
            onValueChange={(val) => form.setValue("category", val, { shouldValidate: true })}
          >
            <SelectTrigger className="h-12 rounded font-bold text-base">
              <SelectValue placeholder="카테고리 선택" />
            </SelectTrigger>
            <SelectContent>
              {CATEGORY_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {form.formState.errors.category && (
            <p className="text-sm text-red-500">{form.formState.errors.category.message}</p>
          )}
        </div>
        {/* Title */}
        <div className="flex flex-col gap-2">
          <Label required>제목</Label>
          <Controller
            name="title"
            control={form.control}
            render={({ field }) => (
              <Input
                type="text"
                maxLength={100}
                placeholder="제목 입력..."
                className="h-12 text-base"
                value={field.value}
                onChange={(e) => {
                  field.onChange(e.target.value.slice(0, 100));
                }}
                onBlur={(e) => {
                  form.setValue("title", e.target.value.trim(), { shouldValidate: true });
                }}
              />
            )}
          />
          {form.formState.errors.title && (
            <p className="text-sm text-red-500">{form.formState.errors.title.message}</p>
          )}
        </div>
        {/* ck editor */}
        <div className="flex flex-col gap-2">
          <Label required>내용</Label>
          <Controller
            control={form.control}
            name="content"
            render={({ field, fieldState }) => (
              <>
                <RichTextEditor value={field.value} onChange={field.onChange} />

                {fieldState.error && (
                  <p className="text-sm text-red-500">{fieldState.error.message}</p>
                )}
              </>
            )}
          />
        </div>

        {/* Cover Image */}
        <div className="rounded-[16px] flex flex-col gap-2">
          <Label required>사진</Label>
          {coverImagePreview ? (
            <div
              className="relative w-full h-[200px] rounded-lg overflow-hidden cursor-pointer"
              onClick={() => coverReplaceInputRef.current?.click()}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
            >
              <SafeImage
                src={coverImagePreview}
                alt="cover"
                className="w-full h-full object-cover pointer-events-none"
              />
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleRemoveCoverImage();
                }}
                className="absolute top-2 right-2 w-8 h-8 bg-black/50 rounded-full flex items-center justify-center"
              >
                <img src={IconX} alt="remove" className="w-4 h-4 invert" />
              </button>
              <input
                ref={coverReplaceInputRef}
                type="file"
                accept=".png,.jpg,.jpeg,.gif"
                className="hidden"
                onChange={handleCoverImageChange}
              />
            </div>
          ) : (
            <label
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              className={`w-full h-[216px] rounded-[8px] border border-dashed flex flex-col items-center justify-center cursor-pointer transition-colors ${
                isDragging
                  ? "border-[#FF3B00] bg-[#ff3b000a]"
                  : "border-[rgba(0,0,0,0.15)] hover:bg-[#00000004]"
              }`}
            >
              <img src={IconUpload} alt="upload" className="w-6 h-6 mb-3" />
              <p className="text-base font-bold text-[#000000] mb-1">
                이미지를 여기로 드래그하거나, 클릭해서 선택하세요.
              </p>
              <p className="text-sm font-normal text-[#B0B0B0] mb-4">PNG, JPG, GIF up to 10MB</p>
              <span className="px-4 py-2 rounded bg-[#FBE9D6] text-[#000000] text-sm font-bold">
                파일 선택
              </span>
              <input
                type="file"
                accept=".png,.jpg,.jpeg,.gif"
                className="hidden"
                onChange={handleCoverImageChange}
              />
            </label>
          )}

          {form.formState.errors.coverImage && !coverImageError && (
            <p className="text-sm text-red-500">
              {String(form.formState.errors.coverImage.message)}
            </p>
          )}
          {coverImageError && <p className="text-sm text-red-500">{coverImageError}</p>}
        </div>

        {/* Publish At */}
        <div className=" rounded-[16px]  flex flex-col gap-3">
          <Label>게시 시간</Label>
          <Controller
            name="publish_at"
            control={form.control}
            render={({ field }) => (
              <DateTimePicker
                value={field.value ? new Date(field.value) : undefined}
                onChange={(date) => field.onChange(date.toISOString())}
                onClear={() => field.onChange("")}
              />
            )}
          />
        </div>
      </div>

      <ConfirmDialog
        open={showDuplicateDialog}
        onOpenChange={setShowDuplicateDialog}
        iconVariant="warning"
        title="중복된 제목입니다"
        description="이미 동일한 제목으로 등록된 게시글이 존재합니다. 다른 제목을 입력하거나 기존 게시글을 확인해 주세요."
        primaryAction={{ label: "그대로 등록", onClick: handleForceCreate }}
        secondaryAction={{ label: "취소", onClick: () => setShowDuplicateDialog(false) }}
      />
    </div>
  );
};

export default AxNewsForm;
